/**
 * Framework-free logic for product-instance attribute fields.
 *
 * The beta API stores every attribute value as a STRING on the wire: the GET
 * `current_value` is a string and the PUT accepts a bare JSON string. These
 * helpers map an attribute's declared type to how we round-trip that string —
 * the transport always stays a string. Keep this module UI-framework-agnostic
 * (no React / antd / moment); the presentational control layer consumes it.
 *
 * Type values are the API's wire strings (gandalf `AttributeType.getApiValue()`:
 * "Boolean" / "Text" / "Number" / "DateTime"), exposed as `attribute_definition_type`.
 */

export const ATTR_TYPE = {
  BOOLEAN: "Boolean",
  TEXT: "Text",
  NUMBER: "Number",
  DATE_TIME: "DateTime",
};

// Canonical boolean strings — match gandalf's Boolean.toString() ("true"/"false").
export const BOOL_TRUE = "true";
export const BOOL_FALSE = "false";

// Value(s) that count as boolean TRUE, compared lower-cased + trimmed — so any
// case of "true" ("TRUE", "True", " true ") matches, which is what gandalf emits.
// Everything else — including bad/mismatched stored data — reads as false / "No".
// Kept as a set so it's a one-line change if the server ever emits other truthy
// encodings (mirror any addition in attributeValueLogic.test.js).
export const TRUTHY_BOOL_VALUES = new Set([BOOL_TRUE]);

/**
 * Normalize a raw `attribute_definition_type` to one of ATTR_TYPE, or null when
 * absent/unknown (caller falls back to a plain text input). Lenient on case.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeAttrType(raw) {
  switch (String(raw ?? "").trim().toLowerCase()) {
    case "boolean":
      return ATTR_TYPE.BOOLEAN;
    case "text":
      return ATTR_TYPE.TEXT;
    case "number":
      return ATTR_TYPE.NUMBER;
    case "datetime":
      return ATTR_TYPE.DATE_TIME;
    default:
      return null;
  }
}

/**
 * "No value" test: null, undefined, empty, or whitespace-only all count as blank
 * (i.e. null). Used so a boolean field shows nothing selected — distinct from an
 * explicit false — for any of those.
 */
export function isBlankValue(v) {
  return v === null || v === undefined || String(v).trim() === "";
}

/** Whether a stored string represents boolean true (see TRUTHY_BOOL_VALUES). */
export function isTruthyBoolString(v) {
  return TRUTHY_BOOL_VALUES.has(String(v ?? "").trim().toLowerCase());
}

/** Map a numeric input value to its wire string ("" when cleared). */
export function numberToWire(num) {
  return num === null || num === undefined || num === "" ? "" : String(num);
}

/**
 * Map a date picker's value (a moment-like object, or null) to its wire string —
 * an ISO-8601 UTC timestamp, or "" when cleared. (DATE_TIME wire format is pending
 * gandalf confirmation — see handoff; centralize any change here.)
 */
export function dateToWire(m) {
  return m ? m.toISOString() : "";
}

/**
 * Java `ZonedDateTime.toString()` can append a region suffix like
 * "[Europe/London]" that is not valid ISO-8601. Strip it so a date parser can
 * read the offset portion. (NOTE: the exact DATE_TIME wire format is pending
 * confirmation from gandalf — see docs/handoffs; centralize any format change here.)
 * @param {unknown} s
 * @returns {string}
 */
export function stripZoneRegion(s) {
  return String(s ?? "").replace(/\[[^\]]*\]\s*$/, "");
}

/**
 * Compare an edited attribute value against a baseline value. null / undefined /
 * "" are all treated as "empty" (equivalent), and arrays compare by content — so
 * an edit reverted back to the baseline reads as "no change". Backs the dirty /
 * unsaved-changes detection on the product detail page.
 */
export function sameAttrValue(a, b) {
  const norm = (v) => {
    if (v === null || v === undefined || v === "") return "";
    if (Array.isArray(v)) return JSON.stringify(v);
    return String(v);
  };
  return norm(a) === norm(b);
}
